import {
  AccessField,
  Binary,
  Call,
  Convert,
  Group,
  Identifier,
  Negate,
  Num,
  type ExpressionVisitor,
  Str,
  type Expression,
  Bool,
  CalendarValue,
} from './ast.ts';

export type ExpressionSubstitutorResult = (string | { name: string; value: string })[];

export class ExpressionSubstitutor implements ExpressionVisitor<void> {
  /**
   * This function turns the expression into an array of strings, except each variable is annotated with
   * both its name and its value, so that it is easy for people to see what the final computation ended up being.
   */
  static substitute(
    expression: Expression,
    variables: Map<string, Num | Str | Bool | unknown>,
  ): ExpressionSubstitutorResult {
    return new ExpressionSubstitutor(expression, variables).evaluate();
  }

  public result: ExpressionSubstitutorResult = [];
  public readonly expression: Expression;
  public readonly variables: Map<string, Num | Str | Bool | unknown>;

  constructor(expression: Expression, variables: Map<string, Num | Str | Bool | unknown>) {
    this.expression = expression;
    this.variables = variables;
  }

  evaluate(): ExpressionSubstitutorResult {
    this.result = [];
    this.expression.accept(this);
    return this.result;
  }

  visitNum(node: Num): void {
    this.result.push(node.toString());
  }

  visitStr(node: Str): void {
    this.result.push(`'${node.value}'`);
  }

  visitBool(node: Bool): void {
    this.result.push(node.toString());
  }

  visitCalendarValue(node: CalendarValue): void {
    this.result.push(node.toString());
  }

  visitIdentifier(node: Identifier): void {
    const val = this.variables.get(node.nameWithAnnotation);
    if (!val) {
      throw new Error(`Could not find identifier: ${node.nameWithAnnotation}`);
    }

    if (val && typeof val === 'object') {
      if (val instanceof Num) {
        this.result.push({ name: node.nameWithAnnotation, value: val.toString() });
        return;
      } else if (val instanceof Str) {
        this.result.push({ name: node.nameWithAnnotation, value: '"' + val.value + '"' });
        return;
      }
    }

    this.result.push({ name: node.nameWithAnnotation, value: String(val) });
  }

  visitAccessField(node: AccessField): void {
    node.expr.accept(this);
    this.result.push('.' + node.name.nameWithAnnotation);
  }

  visitNegate(node: Negate): void {
    this.result.push('-');
    node.expr.accept(this);
  }

  visitCall(node: Call): void {
    if (node.callee instanceof Identifier) {
      this.result.push(node.callee.nameWithAnnotation);
    } else {
      node.callee.accept(this);
    }

    this.result.push('(');
    for (const arg of node.args) {
      arg.accept(this);
    }
    this.result.push(')');
  }

  visitGroup(node: Group): void {
    this.result.push('(');
    node.expr.accept(this);
    this.result.push(')');
  }

  visitConvert(node: Convert): void {
    node.expr.accept(this);
    this.result.push('-> ' + node.units.toString());
  }

  visitBinary(node: Binary): void {
    node.left.accept(this);
    this.result.push(node.operator);
    node.right.accept(this);
  }
}
